import { API_ROUTES } from '@config/routes';
import type { Project } from '@store/slices/projectSlice';
import type {
  FormBuilderElement,
  FormQuestionnaireField,
  FormQuoteSummary,
  FormType,
  FormStatus,
} from '@/pages/sidebar/FormsPage/types';
import type { AppliedInvoiceDiscount, PaymentPlanInstallment } from '@/types/invoicing';
import api from './api';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProjectInvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface ProjectInvoicePayment {
  id: string;
  amount: number;
  method: 'cash' | 'card' | 'transfer' | 'other';
  note?: string;
  paidAt: string;
}

export interface ProjectInvoicePendingPaymentRequest {
  id: string;
  amount: number;
  method: 'card' | 'transfer';
  note?: string | null;
  paidAt: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'declined';
  clientName?: string | null;
  clientEmail?: string | null;
  source?: 'client_portal' | 'manual';
}

export interface ProjectInvoice {
  id: string;
  projectId: string;
  invoiceNumber: string;
  projectName: string;
  contactName: string;
  contactEmail: string;
  amount: number;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  dueDate?: string;
  invoiceDate?: string;
  paidAt?: string;
  payments?: ProjectInvoicePayment[];
  pendingPaymentRequests?: ProjectInvoicePendingPaymentRequest[];
  items: ProjectInvoiceLineItem[];
  notes?: string;
  paymentTerms?: string;
  taxRate?: number;
  discount?: number;
  invoiceShareToken?: string | null;
  portalShareToken?: string | null;
  appliedDiscounts?: AppliedInvoiceDiscount[];
  paymentScheduleId?: string;
  paymentPlan?: PaymentPlanInstallment[];
  paymentPlanLockedAt?: string | null;
  allowClientPaymentPlanChange?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectForm {
  id: string;
  title: string;
  type: FormType;
  status: FormStatus;
  updatedAt: string;
  projectId?: string;
  createdAt?: string;
  userId?: string;
  content?: string;
  templateId?: string;
  templateRef?: string;
  formShareToken?: string | null;
  signedAt?: string;
  signedLegalName?: string;
  businessName?: string;
  businessEmail?: string | null;
  companyLogoUrl?: string | null;
  quoteSummary?: FormQuoteSummary;
  questionnaireFields?: FormQuestionnaireField[];
  questionnaireResponses?: Record<string, string>;
  questionnaireSubmittedAt?: string | null;
  deletedAt?: string | null;
  scheduledDeletionAt?: string | null;
  builderElements?: FormBuilderElement[];
}

// ── Service ───────────────────────────────────────────────────────────────────

export const projectService = {
  // Projects
  getProjects: (options?: { includeDeleted?: boolean }): Promise<Project[]> =>
    api.get<Project[]>(API_ROUTES.PROJECTS.BASE, {
      params: {
        includeDeleted: options?.includeDeleted ?? false,
      },
    }),

  getAllInvoices: (): Promise<ProjectInvoice[]> =>
    api.get<ProjectInvoice[]>(API_ROUTES.PROJECTS.ALL_INVOICES),

  getProjectById: (id: string): Promise<Project> => api.get<Project>(API_ROUTES.PROJECTS.BY_ID(id)),

  createProject: (data: Partial<Project>): Promise<Project> =>
    api.post<Project>(API_ROUTES.PROJECTS.BASE, data),

  updateProject: (id: string, data: Partial<Project>): Promise<Project> =>
    api.put<Project>(API_ROUTES.PROJECTS.BY_ID(id), data),

  updateAssignments: (id: string, memberIds: string[]): Promise<Project> =>
    api.put<Project>(API_ROUTES.PROJECTS.ASSIGNMENTS(id), { memberIds }),

  deleteProject: (id: string): Promise<Project> =>
    api.delete<Project>(API_ROUTES.PROJECTS.BY_ID(id)),

  restoreProject: (id: string): Promise<Project> =>
    api.post<Project>(API_ROUTES.PROJECTS.RESTORE(id)),

  permanentlyDeleteProject: (id: string): Promise<void> =>
    api.delete<void>(API_ROUTES.PROJECTS.PERMANENT_DELETE(id)),

  // Invoices
  getInvoices: (projectId: string): Promise<ProjectInvoice[]> =>
    api.get<ProjectInvoice[]>(API_ROUTES.PROJECTS.INVOICES(projectId)),

  createInvoice: (projectId: string, data: Omit<ProjectInvoice, 'id'>): Promise<ProjectInvoice> =>
    api.post<ProjectInvoice>(API_ROUTES.PROJECTS.INVOICES(projectId), data),

  updateInvoice: (
    projectId: string,
    invoiceId: string,
    data: Partial<ProjectInvoice>
  ): Promise<ProjectInvoice> =>
    api.put<ProjectInvoice>(API_ROUTES.PROJECTS.INVOICE_BY_ID(projectId, invoiceId), data),

  deleteInvoice: (projectId: string, invoiceId: string): Promise<void> =>
    api.delete<void>(API_ROUTES.PROJECTS.INVOICE_BY_ID(projectId, invoiceId)),

  // Forms
  getForms: (projectId: string, options?: { includeDeleted?: boolean }): Promise<ProjectForm[]> =>
    api.get<ProjectForm[]>(API_ROUTES.PROJECTS.FORMS(projectId), {
      params: {
        includeDeleted: options?.includeDeleted ?? false,
      },
    }),

  createForm: (projectId: string, data: Omit<ProjectForm, 'id'>): Promise<ProjectForm> =>
    api.post<ProjectForm>(API_ROUTES.PROJECTS.FORMS(projectId), data),

  updateForm: (
    projectId: string,
    formId: string,
    data: Partial<ProjectForm>
  ): Promise<ProjectForm> =>
    api.put<ProjectForm>(API_ROUTES.PROJECTS.FORM_BY_ID(projectId, formId), data),

  deleteForm: (projectId: string, formId: string): Promise<void> =>
    api.delete<void>(API_ROUTES.PROJECTS.FORM_BY_ID(projectId, formId)),

  permanentlyDeleteForm: (projectId: string, formId: string): Promise<void> =>
    api.delete<void>(`${API_ROUTES.PROJECTS.FORM_BY_ID(projectId, formId)}/permanent`),
};
